import React, { createContext, useState, useContext, useEffect } from "react";
import axiosInstance from "../axiosInstace";
import axios from "axios";
import { useNavigate } from "react-router-dom"; // Add this at the top

// Enable or disable debugging globally
const DEBUG = true;

const logDebug = (label, data) => {
  if (DEBUG) {
    console.groupCollapsed(
      `%c[DEBUG] ${label}`,
      "color: #3b82f6; font-weight: bold;",
    );
    console.log(data);
    console.groupEnd();
  }
};

/**
 * @typedef {object} ModuleAccess
 * @property {string} moduleAccessID
 * @property {string[]} moduleFeatureAccessIDs
 */

/**
 * @typedef {object} User
 * @property {string} status
 * @property {string} employeeName
 * @property {string} username
 * @property {string[]} authorities
 * @property {string} role
 * @property {string} colorTheme
 * @property {string | null} profileImageName
 * @property {string | null} profileImageType
 * @property {string | null} profileImage
 * @property {string} companyName
 * @property {string | null} logoName
 * @property {string | null} logoType
 * @property {string | null} logo
 * @property {ModuleAccess[]} moduleAccess
 */

/**
 * @typedef {object} AuthorizationConfig
 * @property {string[]} [roles] - Required roles for access
 * @property {string} [module] - Required module for access
 * @property {string} [feature] - Required feature for access
 * @property {boolean} [requireAll] - If true, user must have ALL specified roles/features
 */

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const getStoredAuthorities = () => {
    try {
      const storedAuthorities = sessionStorage.getItem("Authorities");
      return storedAuthorities ? JSON.parse(storedAuthorities) : [];
    } catch (error) {
      console.error("Error parsing stored authorities:", error);
      return [];
    }
  };

  const getStoredRole = () => sessionStorage.getItem("Role") || "";

  const clearSessionStorage = () => {
    sessionStorage.clear();
    logDebug("Session Storage Cleared", {});
  };

  useEffect(() => {
    logDebug("Auth Provider mounted", "Starting session establishment...");

    const establishSessionOnLoad = async () => {
      try {
        // --- Step 1: Refresh the session token ---
        // NOTE: X-TenantID intentionally NOT sent. Backend currently issues
        // and validates all tokens as PUBLIC (see JWTService.generateToken,
        // which always signs with JWT_PUBLIC_SECRET_KEY). Sending a tenant
        // header makes resolveTokenType() pick the TENANT secret for
        // validation, which won't match and causes a signature mismatch.
        // Re-add X-TenantID here once tenant-based signing is implemented.
        const productId = process.env.REACT_APP_PRODUCT_ID;

        logDebug("Attempting to refresh token with credentials", {
          productId,
        });

        const refreshTokenResponse = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/refresh-token`,
          null, // No request body is needed
          {
            headers: {
              "X-ProductID": productId,
            },
            withCredentials: true, // IMPORTANT: This sends the HttpOnly refreshToken cookie
          },
        );

        const newJwtToken =
          refreshTokenResponse.headers["Authorization"] ||
          refreshTokenResponse.headers["authorization"];

        if (newJwtToken) {
          sessionStorage.setItem("jwtToken", newJwtToken);
          logDebug("New JWT Token stored in sessionStorage.", newJwtToken);
        } else {
          throw new Error("No JWT token received from refresh endpoint.");
        }

        // --- Step 2: Fetch user access rights with the new token ---
        logDebug("Token refreshed. Now fetching user access data...");

        const accessResponse = await axiosInstance.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/access`,
        );

        logDebug("User Access Response", {
          status: accessResponse.status,
          data: accessResponse.data,
        });

        if (accessResponse.data) {
          const userData = {
            ...accessResponse.data,
            authorities:
              accessResponse.data.authorities || getStoredAuthorities(),
            role: accessResponse.data.role || getStoredRole(),
          };
          setUser(userData);
          logDebug("User session fully established.", userData);
          // navigate("/dashboard") removed — this effect restores session state,
          // it shouldn't force navigation away from whatever route the user is on.
        } else {
          throw new Error("API /api/access did not return any user data.");
        }
      } catch (error) {
        logDebug("Session establishment failed", { error });
        console.error("Failed to establish session:", error.message);
        setUser(null);
        sessionStorage.removeItem("jwtToken");
      } finally {
        setIsLoading(false);
        logDebug("Initial loading process finished.");
      }
    };

    establishSessionOnLoad();
  }, [navigate]);

  const login = (userData) => {
    const completeUserData = {
      ...userData,
      authorities: userData.authorities || getStoredAuthorities(),
      role: userData.role || getStoredRole(),
    };

    setUser(completeUserData);
    logDebug("Manual login successful", completeUserData);
  };

  // === CREATOR STATUS ===
  /**
   * Marks the current user as a creator via /api/update/is-creator.
   * The access response is re-read after the update. This keeps all creator
   * UI gated by the server's `/api/access` value rather than an optimistic
   * client-side flag.
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  const becomeCreator = async () => {
    try {
      logDebug("Become Creator - Request", { url: "/api/update/is-creator" });
      const response = await axiosInstance.put("/api/update/is-creator");
      logDebug("Become Creator - Response", response.data);

      const accessResponse = await axiosInstance.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/access`,
      );
      const accessData = accessResponse.data;

      if (!accessData?.isCreator) {
        throw new Error("Creator access was not confirmed by /api/access.");
      }

      setUser((prev) => ({
        ...prev,
        ...accessData,
        authorities: accessData.authorities || prev?.authorities || [],
        role: accessData.role || prev?.role || "",
      }));

      return { success: true, data: accessData };
    } catch (error) {
      console.error("Failed to update creator status:", error);
      logDebug("Become Creator - Error", { error });
      return { success: false, error };
    }
  };

  const logout = async () => {
    const url = "/api/logout";
    try {
      logDebug("Logout - Request", { url, method: "POST" });
      await axiosInstance.post(url);
      logDebug("Logout Successful", {});
    } catch (error) {
      console.error("Logout Error:", error);
    } finally {
      setUser(null);
      clearSessionStorage();
      window.location.href = "/";
    }
  };

  // === ROLE-BASED ACCESS CONTROL ===
  const hasRole = (role) => user?.authorities?.includes(role) ?? false;
  const hasAuthority = (authority) =>
    user?.authorities?.includes(authority) ?? false;
  const hasAllAuthorities = (authorities) =>
    authorities.every((auth) => user?.authorities?.includes(auth));
  const hasAnyAuthority = (authorities) =>
    authorities.some((auth) => user?.authorities?.includes(auth));

  // === MODULE-BASED ACCESS CONTROL ===
  const canAccessModule = (moduleId) =>
    user?.moduleAccess?.some((module) => module.moduleAccessID === moduleId) ??
    false;

  /**
   * Checks if the user has access to a specific module feature.
   * @param {string} featureId - The ID of the feature to check (e.g., "ESS_MY_LEAVE").
   * @returns {boolean} - True if the user has access, false otherwise.
   */
  const canAccessModuleFeature = (featureId) => {
    if (!user || !user.moduleAccess) return false;
    // Flatten all feature IDs from all accessible modules into a single array
    const allFeatureIds = user.moduleAccess.flatMap(
      (module) => module.moduleFeatureAccessIDs,
    );
    // Check if the requested featureId is in the list of all accessible features
    return allFeatureIds.includes(featureId);
  };

  /**
   * Check if user has access to a specific module and feature combination
   * @param {string} moduleId - Module ID (e.g., "LEAVE", "ATTENDANCE")
   * @param {string} featureId - Feature ID (e.g., "ESS_MY_LEAVE")
   * @returns {boolean}
   */
  const hasModuleAccess = (moduleId, featureId = null) => {
    if (!user || !user.moduleAccess) return false;

    const moduleEntry = user.moduleAccess.find(
      (mod) => mod.moduleAccessID === moduleId,
    );

    if (!moduleEntry) return false;

    // If feature is not provided, grant access to the whole module
    if (!featureId) return true;

    return moduleEntry.moduleFeatureAccessIDs.includes(featureId);
  };

  // === UNIFIED AUTHORIZATION SYSTEM ===
  /**
   * Comprehensive authorization check that handles both role and module-based access
   * @param {AuthorizationConfig} config - Authorization configuration
   * @returns {boolean} - True if user is authorized, false otherwise
   */
  const isAuthorized = (config = {}) => {
    if (!user) return false;

    const { roles = [], module, feature, requireAll = false } = config;

    // If no authorization requirements specified, allow access
    if (!roles.length && !module && !feature) return true;

    let roleCheck = true;
    let moduleCheck = false;

    // Role-based check
    if (roles.length > 0) {
      roleCheck = requireAll
        ? roles.every((role) => user.authorities?.includes(role))
        : roles.some((role) => user.authorities?.includes(role));
    }

    // Module/Feature-based check
    if (module || feature) {
      if (module && feature) {
        // Both module and feature specified
        moduleCheck = hasModuleAccess(module, feature);
      } else if (feature) {
        // Only feature specified (for cases like ESS)
        moduleCheck = canAccessModuleFeature(feature);
      } else if (module) {
        // Only module specified
        moduleCheck = canAccessModule(module);
      }
    }

    // Return based on requireAll flag
    return requireAll ? roleCheck && moduleCheck : roleCheck || moduleCheck;
  };

  // === UTILITY FUNCTIONS ===
  const getUserAuthorities = () => user?.authorities || [];
  const getUserRole = () => user?.role || "";

  /**
   * Filter menu items based on authorization
   * @param {Array} menuItems - Array of menu items with authorization config
   * @returns {Array} - Filtered menu items
   */
  const filterMenuItems = (menuItems) => {
    return menuItems.filter((item) => {
      // Handle new format (auth object) - prioritize this
      if (item.auth) {
        return isAuthorized(item.auth);
      }

      // Handle legacy format with individual properties
      if (item.roles || item.requiredModules || item.requiredFeature) {
        return isAuthorized({
          roles: item.roles,
          module: item.requiredModules?.[0],
          feature: item.requiredFeature,
        });
      }

      // Handle direct feature check (ESS format)
      if (item.feature) {
        return isAuthorized({ feature: item.feature });
      }

      // Handle features array (ESS format)
      if (item.features) {
        return item.features.some((feature) => isAuthorized({ feature }));
      }

      // No authorization required
      return true;
    });
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,

    // Creator status
    // `user?.isCreator` flows in automatically from whatever /api/access
    // (or login()'s /api/auth payload) returns, since both spread their
    // full response into the user object. This boolean is just a
    // convenient, always-defined (never null/undefined) read of it.
    isCreator: !!user?.isCreator,
    becomeCreator,

    // Role-based functions
    hasRole,
    hasAuthority,
    hasAllAuthorities,
    hasAnyAuthority,

    // Module-based functions
    canAccessModule,
    canAccessModuleFeature,
    hasModuleAccess,

    // Unified authorization & utilities
    isAuthorized,
    filterMenuItems,
    getUserAuthorities,
    getUserRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {!isLoading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
